import React, { useEffect, useState } from 'react'
import {
  Alert,
  Avatar,
  Badge,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Container,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@mui/material'
import { Add, Edit, Inventory2, Refresh, Search } from '@mui/icons-material'
import { apiService } from '../../services/api'
import type { KitInicial, KitInicialItem } from '../../types/palworldItems'
import PalworldKitInicialDrawer, { type PalworldKitInicialLine } from '../../components/palworld/kit-inicial/PalworldKitInicialDrawer'

const PalworldKitsIniciaisPage: React.FC = () => {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [kits, setKits] = useState<KitInicial[]>([])
  const [searchId, setSearchId] = useState('')
  const [searching, setSearching] = useState(false)
  const [searchResult, setSearchResult] = useState<KitInicial | null>(null)

  const [drawerOpen, setDrawerOpen] = useState(false)
  const [drawerLines, setDrawerLines] = useState<PalworldKitInicialLine[]>([])
  const [editingKit, setEditingKit] = useState<KitInicial | null>(null)

  const loadKits = async () => {
    setLoading(true)
    setError(null)
    try {
      const data = await apiService.listarKitsIniciais({ active: true })
      setKits(Array.isArray(data) ? data : [])
    } catch (e: any) {
      setError(e?.message || 'Erro ao carregar kits iniciais.')
      setKits([])
    } finally {
      setLoading(false)
    }
  }

  const searchKitById = async () => {
    const id = searchId.trim()
    if (!id) {
      setError('Digite um ID para buscar.')
      return
    }

    setSearching(true)
    setError(null)
    setSearchResult(null)
    try {
      const data = await apiService.buscarKitInicialPorId(id)
      setSearchResult(data)
    } catch (e: any) {
      setError(e?.message || 'Kit não encontrado.')
      setSearchResult(null)
    } finally {
      setSearching(false)
    }
  }

  const handleEditKit = (kit: KitInicial) => {
    setEditingKit(kit)
    const lines: PalworldKitInicialLine[] = []
    if (kit.itens && Array.isArray(kit.itens)) {
      for (const item of kit.itens) {
        if (item.item) {
          lines.push({
            id: item.itemId,
            item: item.item,
            quantity: item.quantidade
          })
        }
      }
    }
    setDrawerLines(lines)
    setDrawerOpen(true)
  }

  const handleCreateNew = () => {
    setEditingKit(null)
    setDrawerLines([])
    setDrawerOpen(true)
  }

  const handleDrawerClose = () => {
    setDrawerOpen(false)
    setEditingKit(null)
    setDrawerLines([])
  }

  useEffect(() => {
    loadKits()
  }, [])

  const totalItemsInKit = (kit: KitInicial) => {
    if (!kit.itens || !Array.isArray(kit.itens)) return 0
    return kit.itens.reduce((sum, item) => sum + (Number(item.quantidade) || 0), 0)
  }

  return (
    <Container maxWidth="xl" sx={{ py: { xs: 3, md: 5 } }}>
      <Stack spacing={2.5}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 900, display: 'flex', alignItems: 'center', gap: 1 }}>
              <Inventory2 />
              Kits Iniciais
            </Typography>
            <Typography sx={{ color: 'text.secondary' }}>
              Gerencie os kits iniciais para novos jogadores.
            </Typography>
          </Box>

          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              onClick={loadKits}
              startIcon={<Refresh />}
              disabled={loading}
            >
              Atualizar
            </Button>
            <Button
              variant="contained"
              onClick={handleCreateNew}
              startIcon={<Add />}
              sx={{ fontWeight: 900 }}
            >
              Novo Kit
            </Button>
          </Stack>
        </Box>

        <Card>
          <CardContent sx={{ p: { xs: 3, md: 3 } }}>
            <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
              <TextField
                size="small"
                label="Buscar por ID"
                value={searchId}
                onChange={(e) => setSearchId(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') searchKitById()
                }}
                placeholder="Ex: 6999dfd2ba4d5d624537dc15"
                sx={{ flex: 1 }}
              />
              <Button
                variant="contained"
                onClick={searchKitById}
                startIcon={<Search />}
                disabled={searching || !searchId.trim()}
              >
                Buscar
              </Button>
            </Stack>
          </CardContent>
        </Card>

        {searchResult && (
          <Card sx={{ border: '2px solid #4caf50' }}>
            <CardContent sx={{ p: { xs: 3, md: 4 } }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 900, mb: 1 }}>
                    {searchResult.nome}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    ID: {searchResult.id}
                  </Typography>
                </Box>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => handleEditKit(searchResult)}
                  startIcon={<Edit />}
                >
                  Editar
                </Button>
              </Box>

              <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
                {searchResult.descricao || 'Sem descrição'}
              </Typography>

              <Typography variant="subtitle2" sx={{ fontWeight: 900, mb: 1 }}>
                Itens do Kit ({totalItemsInKit(searchResult)})
              </Typography>

              <Stack spacing={1}>
                {searchResult.itens?.map((item: KitInicialItem, idx: number) => (
                  <Box
                    key={`${item.itemId}-${idx}`}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                      p: 1.5,
                      borderRadius: 2,
                      border: '1px solid rgba(255,255,255,0.10)',
                      bgcolor: 'rgba(255,255,255,0.03)'
                    }}
                  >
                    <Avatar
                      variant="rounded"
                      src={item.item?.imageUrl}
                      alt={item.item?.name || item.item?.assetName || 'item'}
                      sx={{ width: 40, height: 40, bgcolor: 'rgba(255,255,255,0.08)' }}
                    />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" sx={{ fontWeight: 900 }} noWrap>
                        {item.item?.name || '—'}
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'text.secondary' }} noWrap>
                        {item.item?.assetName || '—'}
                      </Typography>
                    </Box>
                    <Typography variant="body2" sx={{ fontWeight: 900, color: '#4caf50' }}>
                      x{item.quantidade}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            </CardContent>
          </Card>
        )}

        <Card>
          <CardContent sx={{ p: { xs: 3, md: 4 } }}>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <>
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Nome</TableCell>
                        <TableCell>Descrição</TableCell>
                        <TableCell align="right">Itens</TableCell>
                        <TableCell align="right">Criado em</TableCell>
                        <TableCell align="right">Ações</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {kits.map((kit) => (
                        <TableRow key={kit.id} hover>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 900 }}>
                              {kit.nome}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ color: 'text.secondary', maxWidth: 200 }} noWrap>
                              {kit.descricao || '—'}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
                              <Badge badgeContent={totalItemsInKit(kit)} color="primary">
                                <Inventory2 />
                              </Badge>
                            </Box>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                              {kit.createdAt ? new Date(kit.createdAt).toLocaleDateString('pt-BR') : '—'}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => handleEditKit(kit)}
                              startIcon={<Edit />}
                            >
                              Editar
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}

                      {kits.length === 0 && !error && (
                        <TableRow>
                          <TableCell colSpan={5}>
                            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                              Nenhum kit inicial encontrado.
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            )}
          </CardContent>
        </Card>
      </Stack>

      <PalworldKitInicialDrawer
        open={drawerOpen}
        onClose={handleDrawerClose}
        lines={drawerLines}
        setLineQuantity={(id, qty) => {
          setDrawerLines((prev) => prev.map((l) => (l.id === id ? { ...l, quantity: qty } : l)))
        }}
        removeLine={(id) => setDrawerLines((prev) => prev.filter((l) => l.id !== id))}
        clearCart={() => setDrawerLines([])}
        kitInicial={editingKit}
      />
    </Container>
  )
}

export default PalworldKitsIniciaisPage
